import React from "react";
import { useNavigate } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Chip } from "@mui/material";
import { isPendingCancellation } from "../../utils/subscriptionDisplayStatus";
import PriceUpdatedBadge from "../MembershipAnalytics/PriceUpdatedBadge";
import { priceChangesFor } from "../../utils/subscriptionPriceChanges";

function formatDate(dateString) {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const getCustomer = (subscription) => {
  const vehicleCustomer = subscription.vehicle?.customer;
  if (vehicleCustomer && typeof vehicleCustomer === "object") {
    return vehicleCustomer;
  }
  return null;
};

const SubscriptionList = ({ subscriptions, priceChanges }) => {
  const navigate = useNavigate();

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: "fit-content",
        maxWidth: "calc(100vw - 200px)",
        margin: "0 auto",
        overflowX: "auto",
        "&::-webkit-scrollbar": {
          height: "8px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "4px",
          "&:hover": {
            background: "#555",
          },
        },
      }}
    >
      <Table
        sx={{
          minWidth: 900,
          borderCollapse: "separate",
          "td, th": { borderBottom: "1px solid rgba(224, 224, 224, 1)" },
        }}
        aria-label="subscriptions table"
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>Created</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Customer</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Vehicle</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Product</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Valid Until</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Next Billing</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {subscriptions.map((subscription) => {
            const customer = getCustomer(subscription);
            const vehicle = subscription.vehicle;
            const isDeleted = subscription.deleted === "yes" || subscription.deleted === true;
            const pendingCancellation = isPendingCancellation(subscription);
            const isActive = !!subscription.active && !isDeleted;

            return (
              <TableRow
                key={subscription._id}
                onClick={() => navigate(`/subscription/${subscription._id}`)}
                sx={{
                  cursor: "pointer",
                  backgroundColor: pendingCancellation
                    ? "#fff8e1"
                    : !isActive
                      ? isDeleted
                        ? "#ffebee"
                        : "#fff3e0"
                      : "inherit",
                  opacity: !isActive && !pendingCancellation ? 0.85 : 1,
                  "&:hover": {
                    backgroundColor: pendingCancellation
                      ? "#ffecb3"
                      : !isActive
                        ? isDeleted
                          ? "#ffcdd2"
                          : "#ffe0b2"
                        : "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <TableCell>{formatDate(subscription.createdOnUTC)}</TableCell>
                <TableCell>
                  {customer
                    ? `${customer.firstName || ""} ${customer.lastName || ""}`.trim() || customer.email || "N/A"
                    : "N/A"}
                </TableCell>
                <TableCell>
                  {vehicle?.rego || "N/A"}
                  {vehicle?.vin ? ` (${vehicle.vin})` : ""}
                </TableCell>
                <TableCell>
                  {subscription.product?.name || "N/A"}
                  <div>
                    <PriceUpdatedBadge changes={priceChangesFor(priceChanges, subscription._id)} />
                  </div>
                </TableCell>
                <TableCell>{formatDate(subscription.validUntilDateUTC)}</TableCell>
                <TableCell>{formatDate(subscription.nextBillingDateUTC)}</TableCell>
                <TableCell>
                  <Chip
                    label={
                      pendingCancellation
                        ? "Pending cancellation"
                        : isActive
                          ? "Active"
                          : isDeleted
                            ? "Deleted"
                            : "Inactive"
                    }
                    color={
                      pendingCancellation ? "warning" : isActive ? "success" : isDeleted ? "error" : "warning"
                    }
                    size="small"
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SubscriptionList;
